import React, { useState } from 'react';
import { Button, Row, Col, Card } from 'react-bootstrap';
import { ArrowRepeat, CheckCircleFill, Eye, Printer, XCircleFill } from 'react-bootstrap-icons';
import { docHubSourceOptions } from '../../helpers/constants';
import ContactsPicker from '../ContactsPicker';
import ContractsPicker from '../ContractsPicker';
import InventoryPicker from '../inventory/InventoryPicker';
import CategorizedDropdown from '../common/CategorizedDropdown';
import './PrintGroupSelectionBox.scss';

const formatSyncedAt = lastSyncedAt => {
  if (!lastSyncedAt) return null;
  try {
    return new Date(lastSyncedAt).toLocaleString();
  } catch (e) {
    return String(lastSyncedAt);
  }
};

/** True when a cached layout exists and Preview can use it. */
const isLayoutUsable = layoutStatus =>
  Boolean(layoutStatus?.lastSyncedAt) || layoutStatus?.status === 'ready';

const LayoutSyncStatus = ({ layoutStatus, syncingLayout }) => {
  const syncedLabel = formatSyncedAt(layoutStatus?.lastSyncedAt);
  const isSyncing = Boolean(syncingLayout || layoutStatus?.syncInProgress);
  const usable = isLayoutUsable(layoutStatus);

  let statusClass = 'is-pending';
  let message = 'Layout not synced yet';

  if (usable && syncedLabel) {
    statusClass = 'is-ready';
    message = `Layout synced ${syncedLabel}`;
    if (layoutStatus.sheetCount != null) {
      message += ` · ${layoutStatus.sheetCount} sheets`;
    }
    if (isSyncing) {
      message += ' · Syncing update…';
    }
  } else if (isSyncing) {
    statusClass = 'is-syncing';
    message = 'Syncing layout…';
  } else if (layoutStatus?.status === 'failed') {
    statusClass = 'is-failed';
    message = `Layout sync failed${
      layoutStatus.lastSyncError ? `: ${layoutStatus.lastSyncError}` : ''
    }`;
  }

  return (
    <span className={`print-group-selection__sync-status ${statusClass}`}>
      {usable ? (
        <CheckCircleFill className="print-group-selection__sync-icon is-ok" aria-hidden />
      ) : (
        <XCircleFill className="print-group-selection__sync-icon is-missing" aria-hidden />
      )}
      <span>{message}</span>
    </span>
  );
};

const SelectionBox = ({
  showDocSourceSelector,
  docSource,
  onDocSourceChange,
  selectedGroup,
  onGroupPicked,
  selectedDocument,
  documents = [],
  onDocumentChange,
  onPreviewClick,
  onSyncLayoutClick,
  canSyncLayout,
  layoutStatus,
  syncingLayout,
  onSwitchToExportedContractClick,
  documentFieldsLoading,
  printingBlankDocument,
  onPrintBlankDocumentClick,
  docHubVersion = 'v2',
  onDocHubVersionChange
}) => {
  const [pickerMeta, setPickerMeta] = useState(null);
  const isV2 = docHubVersion === 'v2';

  const onPicked = contract => {
    onGroupPicked(contract[0]);
    onPickerClose();
  };

  const onPickerClose = () => {
    setPickerMeta(null);
  };

  const showSyncMeta = Boolean(isV2 && selectedDocument && (layoutStatus || syncingLayout));

  return (
    <>
      <Card className="border rounded print-group-selection">
        <Card.Header className="bg-primary p-2 d-flex align-items-center justify-content-between">
          <h6 className="mb-0 text-white">Print Groups</h6>
          {onDocHubVersionChange && (
            <div
              className="print-group-selection__version-switch"
              role="group"
              aria-label="DocHub version"
            >
              <button
                type="button"
                className={`print-group-selection__version-btn${!isV2 ? ' is-active' : ''}`}
                onClick={() => onDocHubVersionChange('v1')}
                aria-pressed={!isV2}
              >
                V1
              </button>
              <button
                type="button"
                className={`print-group-selection__version-btn${isV2 ? ' is-active' : ''}`}
                onClick={() => onDocHubVersionChange('v2')}
                aria-pressed={isV2}
              >
                V2
              </button>
            </div>
          )}
        </Card.Header>
        <Card.Body className="p-2 print-group-selection__body">
          <Row>
            {showDocSourceSelector && (
              <Col xs={12} md={4} className="px-3">
                <h6 className="mb-2">Select Source</h6>
                <select
                  value={docSource}
                  className={`form-control form-control-sm`}
                  id={'sourceSelect'}
                  onChange={onDocSourceChange}
                >
                  {docHubSourceOptions.map(option => {
                    return (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    );
                  })}
                </select>
              </Col>
            )}
            <Col xs={12} md={showDocSourceSelector ? 4 : 6} className="px-3 mt-2 mt-md-0">
              <h6 className="mb-2">Select {docSource}</h6>
              <Button size="sm" variant="outline-info" block onClick={() => setPickerMeta({})}>
                {selectedGroup
                  ? selectedGroup['buyer'] ||
                    selectedGroup['serialA'] ||
                    selectedGroup['model']?.modelName ||
                    selectedGroup['model']?.modelNumber ||
                    selectedGroup['sizeA'] ||
                    ''
                  : 'Tap to select'}
              </Button>
              {docSource === 'Contract' && (
                <ContractsPicker
                  submitButtonText="Select"
                  show={pickerMeta !== null}
                  selectedContracts={pickerMeta && pickerMeta.selectedData ? [pickerMeta.selectedData] : []}
                  onContractSelect={contract => setPickerMeta({ ...pickerMeta, selectedData: contract })}
                  onContractPickerClose={onPickerClose}
                  onSubmit={onPicked}
                />
              )}
              {docSource === 'Contact' && (
                <ContactsPicker
                  submitButtonText="Select"
                  show={pickerMeta !== null}
                  selectedContacts={pickerMeta && pickerMeta.selectedData ? [pickerMeta.selectedData] : []}
                  onContactSelect={contract => setPickerMeta({ ...pickerMeta, selectedData: contract })}
                  onContactPickerClose={onPickerClose}
                  onSubmit={onPicked}
                />
              )}

              {docSource === 'Inventory' && (
                <InventoryPicker
                  submitButtonText="Select"
                  show={pickerMeta !== null}
                  selectedUnits={pickerMeta && pickerMeta.selectedData ? [pickerMeta.selectedData] : []}
                  onUnitSelect={inventory => setPickerMeta({ ...pickerMeta, selectedData: inventory })}
                  onInventoryPickerClose={onPickerClose}
                  onSubmit={onPicked}
                />
              )}
            </Col>
            <Col xs={12} md={showDocSourceSelector ? 4 : 6} className="px-3 mt-2 mt-md-0">
              <h6 className="mb-2">Select Document</h6>
              <CategorizedDropdown
                childKeys={['name']}
                onOptionChange={option => onDocumentChange(option?.url)}
                options={documents}
                parentKey={'categoryLabel'}
                activeOption={selectedDocument}
                activeOptionToView={documents.find(document => document?.url === selectedDocument)?.name}
                toBeActiveKey={'url'}
              />
            </Col>

            <Col xs={12} className="mt-3 px-3">
              {docSource === 'Contact' && selectedGroup?.['exportedContract'] ? (
                <div className="d-flex alert alert-info mb-0 p-1 align-items-center">
                  <div className="flex-grow-1 text-left mr-2">
                    <b>This contact has already been contracted. Tap on the button to switch to the Contract.</b>{' '}
                  </div>
                  <Button size="sm" onClick={onSwitchToExportedContractClick}>
                    Switch
                  </Button>
                </div>
              ) : (
                <div className="print-group-selection__actions">
                  <div className="print-group-selection__sync">
                    {isV2 && canSyncLayout && onSyncLayoutClick && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="print-group-selection__sync-btn"
                        onClick={onSyncLayoutClick}
                        disabled={!selectedDocument || syncingLayout || documentFieldsLoading}
                      >
                        <ArrowRepeat className={`mr-2${syncingLayout ? ' spin' : ''}`} />
                        {syncingLayout ? 'Syncing Layout…' : 'Sync Layout'}
                      </Button>
                    )}
                    {isV2 && canSyncLayout && showSyncMeta && (
                      <LayoutSyncStatus layoutStatus={layoutStatus} syncingLayout={syncingLayout} />
                    )}
                  </div>

                  <div className="print-group-selection__primary-actions">
                    <Button
                      size="sm"
                      className="mb-1"
                      onClick={onPreviewClick}
                      disabled={!selectedGroup || !selectedDocument || documentFieldsLoading}
                    >
                      <Eye className="mr-2" />
                      Preview
                    </Button>
                    {onPrintBlankDocumentClick && (
                      <Button
                        size="sm"
                        className="mb-1 ml-2"
                        onClick={onPrintBlankDocumentClick}
                        disabled={!selectedDocument || printingBlankDocument}
                      >
                        <Printer className="mr-2" />
                        {printingBlankDocument ? 'Printing Blank Document...' : 'Print Blank Document'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default SelectionBox;
